import { z } from 'zod';

/**
 * A devbox disk snapshot, mapped from Runloop's `DevboxSnapshotView`.
 *
 * Snapshots — like blueprints — live entirely in Runloop; there is no local
 * table. Org scoping rides on snapshot `metadata` (`source: 'reflex'`,
 * `organizationId`, `orgSlug`), and run lineage on `metadata.agentId` /
 * `metadata.agentName`, which the server mapper lifts into the
 * `sourceAgentId` / `sourceAgentName` fields.
 */
export const SnapshotSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    createTimeMs: z.number(),
    /** The devbox this snapshot was taken from. */
    sourceDevboxId: z.string(),
    /** The agent run whose devbox was snapshotted, when saved through the app. */
    sourceAgentId: z.string().optional(),
    sourceAgentName: z.string().optional(),
    /** User-supplied description of the snapshot (Runloop's `commit_message`). */
    commitMessage: z.string().optional(),
    /** Snapshot size in bytes, relative to the base blueprint. */
    sizeBytes: z.number().optional(),
    metadata: z.record(z.string(), z.string()).optional(),
  })
  .meta({ id: 'Snapshot' });

export type Snapshot = z.infer<typeof SnapshotSchema>;

/**
 * Lifecycle of an asynchronous disk-snapshot operation (Runloop's
 * `DevboxSnapshotAsyncStatusView.status`). Creation returns immediately with
 * the snapshot record; clients poll the status endpoint until it leaves
 * `in_progress`.
 */
export const SnapshotOperationStatusSchema = z.enum([
  'in_progress',
  'error',
  'complete',
  'deleted',
]);

export type SnapshotOperationStatus = z.infer<typeof SnapshotOperationStatusSchema>;

export const SnapshotStatusResponseSchema = z
  .object({
    status: SnapshotOperationStatusSchema,
    errorMessage: z.string().optional(),
    /** The snapshot record once the operation has details to report. */
    snapshot: SnapshotSchema.optional(),
  })
  .meta({ id: 'SnapshotStatusResponse' });

export type SnapshotStatusResponse = z.infer<typeof SnapshotStatusResponseSchema>;

export const CreateSnapshotRequestSchema = z.object({
  name: z.string().min(1).max(100),
  /** Optional description, stored as Runloop's `commit_message` (max 1000 chars). */
  commitMessage: z.string().max(1000).optional(),
});

export type CreateSnapshotRequest = z.infer<typeof CreateSnapshotRequestSchema>;

export const UpdateSnapshotRequestSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  commitMessage: z.string().max(1000).optional(),
});

export type UpdateSnapshotRequest = z.infer<typeof UpdateSnapshotRequestSchema>;
